export type ThemeVariant = 'beauty';

export type ContentType =
  | 'multiShopReview'
  | 'singleShopReview'
  | 'guide'
  | 'lazyPack'
  | 'story';

export const CONTENT_TYPE_META: Record<ContentType, { label: string; shortLabel: string; badgeClass: string }> = {
  multiShopReview: { label: '多店評比', shortLabel: '合集', badgeClass: 'bg-primary/10 text-primary' },
  singleShopReview: { label: '單店評價', shortLabel: '探店', badgeClass: 'bg-amber-50 text-amber-700' },
  guide: { label: '推薦指南', shortLabel: '指南', badgeClass: 'bg-emerald-50 text-emerald-700' },
  lazyPack: { label: '攻略懶人包', shortLabel: '懶人包', badgeClass: 'bg-sky-50 text-sky-700' },
  story: { label: '故事專欄', shortLabel: '故事', badgeClass: 'bg-rose-50 text-rose-700' },
};

export interface MysteryShopperCardConfig {
  mode: 'expertise' | 'stats';
  title: string;
  subtitle?: string;
  ctaLabel: string;
  badgeMap: Record<string, { label: string; color: string }>;
}

export interface ExploreColumnConfig {
  locationsLabel: string;
  topicsLabel: string;
  activitiesLabel: string;
  activities: Array<{ label: string; href: string }>;
}

export interface HeroConfig {
  title: string;
  subtitle: string;
  searchPlaceholder: string;
  categories: Array<{ label: string; icon: string; href: string }>;
  hotSearches: Array<{ label: string; href: string }>;
}

export interface SectionTextConfig {
  title: string;
  subtitle?: string;
  ctaLabel?: string;
}

export interface BrandConfig {
  variant: ThemeVariant;
  hero: HeroConfig;
  latestReviews: SectionTextConfig;
  popularStores: SectionTextConfig;
  hotArticles: SectionTextConfig;
  realReviews: SectionTextConfig;
  top10Guides: SectionTextConfig;
  stylePicks: SectionTextConfig;
  editorsPicks: SectionTextConfig;
  featuredPacks: SectionTextConfig;
  easyPacks: SectionTextConfig;
  mysteryShoppers: MysteryShopperCardConfig;
  exploreMore: SectionTextConfig & ExploreColumnConfig;
  global: {
    viewAll: string;
    readTime: string;
    reviews: string;
    stores: string;
    articles: string;
  };
}

// ===== 美好時光指南：美業版 =====
const hero: HeroConfig = {
  title: '美好時光指南',
  subtitle: '神秘客實地走訪 × 台灣美業公正評比，幫你找到不踩雷的美好體驗',
  searchPlaceholder: '輸入城市 / 美業類型 / 店家名稱',
  categories: [
    { label: '彩妝造型', icon: 'ri-magic-line', href: '/topics/makeup-styling' },
    { label: '形象管理', icon: 'ri-user-voice-line', href: '/topics/image-photo' },
    { label: '穿搭風格', icon: 'ri-shirt-line', href: '/topics/fashion-styling' },
    { label: '按摩舒壓', icon: 'ri-heart-3-line', href: '/topics/wellness-relax' },
    { label: '美容保養', icon: 'ri-leaf-line', href: '/topics/beauty-care' },
    { label: '美甲美睫', icon: 'ri-brush-line', href: '/topics/appearance' },
    { label: '塔羅身心靈', icon: 'ri-moon-line', href: '/topics/mind-body-spirit' },
    { label: '暖罐療癒', icon: 'ri-fire-line', href: '/topics/warm-cupping' },
  ],
  hotSearches: [
    { label: '台北按摩', href: '/search?q=台北按摩' },
    { label: '中山區做臉', href: '/search?q=中山區做臉' },
    { label: '美甲推薦', href: '/search?q=美甲推薦' },
    { label: '霧眉', href: '/search?q=霧眉' },
    { label: '越式洗髮', href: '/search?q=越式洗髮' },
    { label: '塔羅占卜', href: '/search?q=塔羅占卜' },
  ],
};

const exploreActivities = [
  { label: '按摩', href: '/articles?category=按摩' },
  { label: 'SPA', href: '/articles?category=SPA' },
  { label: '做臉', href: '/articles?category=做臉' },
  { label: '美甲', href: '/articles?category=美甲' },
  { label: '美睫', href: '/articles?category=美睫' },
  { label: '霧眉', href: '/articles?category=霧眉' },
  { label: '美髮', href: '/articles?category=美髮' },
  { label: '除毛', href: '/articles?category=除毛' },
  { label: '越式洗髮', href: '/articles?category=越式洗髮' },
  { label: '采耳', href: '/articles?category=采耳' },
  { label: '頭皮護理', href: '/articles?category=頭皮護理' },
  { label: '岩盤浴', href: '/articles?category=岩盤浴' },
  { label: '暖罐', href: '/topics/warm-cupping' },
  { label: '色彩鑑定', href: '/topics/color-analysis' },
  { label: '穿搭', href: '/topics/fashion-styling' },
  { label: '光療舒壓', href: '/topics/mind-body-relief' },
  { label: '塔羅', href: '/articles?category=塔羅' },
  { label: '星盤', href: '/articles?category=星盤' },
  { label: '命理', href: '/articles?category=命理' },
  { label: '瑜珈', href: '/articles?category=瑜珈' },
];

export const beautyConfig: BrandConfig = {
  variant: 'beauty',
  hero,
  latestReviews: { title: '最新文章', subtitle: '神秘客剛出爐的實地走訪評比，搶先看！' },
  popularStores: { title: '超人氣探店' },
  hotArticles: { title: '熱騰騰新文章' },
  realReviews: { title: '最真實評比' },
  top10Guides: { title: 'TOP 10 指南' },
  stylePicks: { title: '美好形象精選', subtitle: '形象管理、穿搭風格、彩妝造型、色彩鑑定——從內到外打造自信光彩' },
  editorsPicks: { title: '七編帶你玩' },
  featuredPacks: { title: '精選懶人包' },
  easyPacks: { title: '一看就會懶人包' },
  mysteryShoppers: {
    mode: 'expertise',
    title: '神秘客給你看',
    subtitle: undefined,
    ctaLabel: '加入成為神秘客',
    badgeMap: {
      '金牌神秘客': { label: '金牌好手', color: 'bg-amber-100 text-amber-700 border-amber-200' },
      '銀牌神秘客': { label: '經驗老手', color: 'bg-slate-100 text-slate-700 border-slate-200' },
      '銅牌神秘客': { label: '新鮮好手', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    },
  },
  exploreMore: {
    title: '全站探索',
    subtitle: '瀏覽所有城市與主題，發現你的下一個美好體驗',
    locationsLabel: '搜尋城市',
    topicsLabel: '搜尋主題',
    activitiesLabel: '熱門分類',
    activities: exploreActivities,
  },
  global: {
    viewAll: '查看全部',
    readTime: '分鐘',
    reviews: '則評價',
    stores: '間店家',
    articles: '篇文章',
  },
};

export function getBrandConfig(): BrandConfig {
  return beautyConfig;
}